import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const STARTUPS_PER_PAGE = 4;

const Startups = () => {
  const [startups, setStartups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [booths, setBooths] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [name, setName] = useState('');
  const [founder, setFounder] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [boothId, setBoothId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [id, setId] = useState('');
  const [editName, setEditName] = useState('');
  const [editFounder, setEditFounder] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editBoothId, setEditBoothId] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editImageFile, setEditImageFile] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return null;
    const fileName = `${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from('assets').upload(fileName, file);
    if (error) {
      alert("Image upload failed: " + error.message);
      throw error;
    }
    const { data: { publicUrl } } = supabase.storage.from('assets').getPublicUrl(fileName);
    return publicUrl;
  };

  const getStartups = async () => {
    const res = await supabase
      .from('startups')
      .select('id,created_at,name_en,founder_name,category_id,booth_id,image_url,categories(name_en),booths(name_en)')
      .order('id', { ascending: false });
    setStartups(res.data || []);
  };

  const getLists = async () => {
    const categoriesRes = await supabase.from('categories').select('id,name_en').order('id');
    const boothsRes = await supabase.from('booths').select('id,name_en').order('id');
    setCategories(categoriesRes.data || []);
    setBooths(boothsRes.data || []);
  };

  useEffect(() => {
    getStartups();
    getLists();
  }, []);

  const createStartup = async (e) => {
    e.preventDefault();
    let uploadedUrl = imageUrl;
    if (imageFile) {
      uploadedUrl = await uploadImage(imageFile);
    }
    await supabase.from('startups').insert([{
      name_en: name,
      founder_name: founder,
      category_id: categoryId ? Number(categoryId) : null,
      booth_id: boothId ? Number(boothId) : null,
      image_url: uploadedUrl,
    }]);
    setName('');
    setFounder('');
    setCategoryId('');
    setBoothId('');
    setImageUrl('');
    setImageFile(null);
    setIsCreateOpen(false);
    await getStartups();
  };

  const editStartup = (startup) => {
    setId(startup.id);
    setEditName(startup.name_en || '');
    setEditFounder(startup.founder_name || '');
    setEditCategoryId(startup.category_id || '');
    setEditBoothId(startup.booth_id || '');
    setEditImageUrl(startup.image_url || '');
    setEditImageFile(null);
    setIsEditOpen(true);
  };

  const updateStartup = async (e) => {
    e.preventDefault();
    let finalImageUrl = editImageUrl;
    if (editImageFile) {
      finalImageUrl = await uploadImage(editImageFile);
    }
    await supabase
      .from('startups')
      .update({
        name_en: editName,
        founder_name: editFounder,
        category_id: editCategoryId ? Number(editCategoryId) : null,
        booth_id: editBoothId ? Number(editBoothId) : null,
        image_url: finalImageUrl,
      })
      .eq('id', id);
    setIsEditOpen(false);
    await getStartups();
  };

  const deleteStartup = async (startupId) => {
    await supabase.from('startups').delete().eq('id', startupId);
    await getStartups();
  };

  const filteredStartups = startups.filter((startup) => {
    const value = search.toLowerCase();
    return startup.id.toString().includes(value) || startup.name_en?.toLowerCase().includes(value) || startup.founder_name?.toLowerCase().includes(value);
  });

  const totalPages = Math.max(1, Math.ceil(filteredStartups.length / STARTUPS_PER_PAGE));
  const startIndex = (currentPage - 1) * STARTUPS_PER_PAGE;
  const paginatedStartups = filteredStartups.slice(startIndex, startIndex + STARTUPS_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage startups" title="Startups" />
        <div className="table_controls_card">
          <label className="table_search" htmlFor="startups-search">
            <img alt="" src={searchIcon} />
            <input
              id="startups-search"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search startups..."
              type="text"
              value={search}
            />
          </label>
          <div className="table_controls_actions">
            <span>{filteredStartups.length} total records</span>
            <button className="table_primary_button" onClick={() => setIsCreateOpen(true)} type="button">
              Add Startup
            </button>
          </div>
        </div>
        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead><tr><th>ID</th><th>Name</th><th>Founder</th><th>Category</th><th>Booth</th><th>Image</th><th>Actions</th></tr></thead>
              <tbody>
                {paginatedStartups.map((startup) => {
                  const category = Array.isArray(startup.categories) ? startup.categories[0] : startup.categories;
                  const booth = Array.isArray(startup.booths) ? startup.booths[0] : startup.booths;
                  return (
                    <tr key={startup.id}>
                      <td>{startup.id}</td>
                      <td>{startup.name_en}</td>
                      <td>{startup.founder_name}</td>
                      <td>{category?.name_en}</td>
                      <td>{booth?.name_en}</td>
                      <td>{startup.image_url}</td>
                      <td>
                        <div className="table_actions">
                          <button
                            aria-label={`Edit startup #${startup.id}`}
                            className="table_icon_button"
                            onClick={() => editStartup(startup)}
                            type="button"
                          >
                            <img alt="" src={editIcon} />
                          </button>
                          <button
                            aria-label={`Delete startup #${startup.id}`}
                            className="table_icon_button table_icon_button_danger"
                            onClick={() => deleteStartup(startup.id)}
                            type="button"
                          >
                            <img alt="" src={deleteIcon} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">Next</button>
          </div>
        </div>
        {isCreateOpen && (
          <div
            aria-labelledby="add-startup-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsCreateOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="add-startup-heading">Add Startup</h2>
                </div>
                <button
                  aria-label="Close add startup form"
                  className="table_modal_close"
                  onClick={() => setIsCreateOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={createStartup}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Name</span>
                    <input onChange={(e) => setName(e.target.value)} placeholder="Name" value={name} />
                  </label>
                  <label className="table_form_field">
                    <span>Founder</span>
                    <input onChange={(e) => setFounder(e.target.value)} placeholder="Founder" value={founder} />
                  </label>
                  <label className="table_form_field">
                    <span>Category</span>
                    <select onChange={(e) => setCategoryId(e.target.value)} value={categoryId}>
                      <option value="">Category</option>
                      {categories.map((category) => <option key={category.id} value={category.id}>{category.name_en}</option>)}
                    </select>
                  </label>
                  <label className="table_form_field">
                    <span>Booth</span>
                    <select onChange={(e) => setBoothId(e.target.value)} value={boothId}>
                      <option value="">Booth</option>
                      {booths.map((booth) => <option key={booth.id} value={booth.id}>{booth.name_en}</option>)}
                    </select>
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Image URL or Upload</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input onChange={(e) => setImageUrl(e.target.value)} placeholder="Image URL" value={imageUrl} />
                      <input accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} type="file" />
                    </div>
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {isEditOpen && (
          <div
            aria-labelledby="edit-startup-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsEditOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="edit-startup-heading">Edit Startup</h2>
                </div>
                <button
                  aria-label="Close edit startup form"
                  className="table_modal_close"
                  onClick={() => setIsEditOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={updateStartup}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Name</span>
                    <input onChange={(e) => setEditName(e.target.value)} value={editName} />
                  </label>
                  <label className="table_form_field">
                    <span>Founder</span>
                    <input onChange={(e) => setEditFounder(e.target.value)} value={editFounder} />
                  </label>
                  <label className="table_form_field">
                    <span>Category</span>
                    <select onChange={(e) => setEditCategoryId(e.target.value)} value={editCategoryId}>
                      <option value="">Category</option>
                      {categories.map((category) => <option key={category.id} value={category.id}>{category.name_en}</option>)}
                    </select>
                  </label>
                  <label className="table_form_field">
                    <span>Booth</span>
                    <select onChange={(e) => setEditBoothId(e.target.value)} value={editBoothId}>
                      <option value="">Booth</option>
                      {booths.map((booth) => <option key={booth.id} value={booth.id}>{booth.name_en}</option>)}
                    </select>
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Image URL or Upload</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input onChange={(e) => setEditImageUrl(e.target.value)} placeholder="Image URL" value={editImageUrl} />
                      <input accept="image/*" onChange={(e) => setEditImageFile(e.target.files[0])} type="file" />
                    </div>
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Startups;
